import { Action, createSelector, Selector, State, StateContext, Store } from '@ngxs/store';
import { Injectable } from '@angular/core';

import { ConcessionControlService } from '../services/concessioncontrol.service';
import {
    OrganizationState,
    OrganizationStateModel,
} from '../../../admin/organization/organization.state';

/** This file is divided in 3 parts:
 * - The definition of the Store : defining data structure
 * - Constants that allow NgXs to trigger Actions
 * - Selectors & Actions : Functions allowed to interact with the store
 *
 */

// Store definition
export interface ConcessionControlModel {
    data: {
        [organization_id: string]: {
            [year: number]: {
                [key: string]: any;
            };
        };
    };
}
// We get data like model[organizationId][year][keyValue]

//Constants

//Action loaded on page init : ask store to retreive all data from a list of keys
export class PrepareDataFromKeys {
    static readonly type = 'CONCESSIONCONTROL/PREPARE_DATA_FOR_PAGE';
    constructor(public payload: { keys: string[] }) {}
}

//Actions
const NAME = 'concessionControl';
@State<ConcessionControlModel>({
    name: NAME,
    defaults: {
        data: {},
    },
})
@Injectable()
export class ConcessioncontrolState {
    constructor(
        private readonly concessionControlService: ConcessionControlService,
        private readonly store: Store,
    ) {}

    public static getDataByKey(key: string) {
        return createSelector(
            [OrganizationState, ConcessioncontrolState],
            (
                organizationState: OrganizationStateModel,
                state: ConcessionControlModel,
            ): { [key: string]: [] }[] => {
                const currentOrganization = organizationState.currentOrganization;
                const currentYear = organizationState.currentYear;
                try {
                    return state.data[currentOrganization?.id!][currentYear][key] || [{}];
                } catch {
                    return [];
                }
            },
        );
    }

    @Action(PrepareDataFromKeys)
    public async prepareDataFromKeys(
        ctx: StateContext<ConcessionControlModel>,
        action: PrepareDataFromKeys,
    ): Promise<void> {
        const { keys } = action.payload;

        await Promise.all(
            keys.map(async (key) => {
                const currentOrganization = this.store.selectSnapshot(
                    OrganizationState.currentOrganization,
                );
                const currentYear = this.store.selectSnapshot(OrganizationState.currentYear);

                if (!currentOrganization || !currentYear) {
                    return;
                }

                const state = ctx.getState();
                state.data[currentOrganization.id] = state.data[currentOrganization.id] || {};
                state.data[currentOrganization.id][currentYear] =
                    state.data[currentOrganization.id][currentYear] || {};

                try {
                    //Ignore if data already exist
                    if (
                        state.data[currentOrganization.id][currentYear][key]?.length >= 0 &&
                        !state.data[currentOrganization.id][currentYear][key][0]?.error
                    ) {
                        return;
                    }
                    const data: [] = await this.concessionControlService.get(
                        currentOrganization.id,
                        currentYear,
                        key,
                    );
                    if (data?.length > 0) {
                        state.data[currentOrganization.id][currentYear][key] = data;
                    } else {
                        state.data[currentOrganization.id][currentYear][key] = [
                            { organizationId: currentOrganization.id, error: true },
                        ];
                    }
                } catch (error: any) {
                    state.data[currentOrganization.id][currentYear][key] = [
                        { organizationId: currentOrganization.id, error: true },
                    ];
                } finally {
                    ctx.patchState(state);
                    return;
                }
            }),
        );
    }
}
